import Mustache from "mustache"
import debounce from "lodash/debounce"
import { addEventListenerWithKey } from "shared/lib/utils.js"
import { linkOrigin } from "app/utils/link_origin"
import { onBFCacheLoad } from "shared/scripts/back_forward_cache_utils"
import { fetchAllServices, isJsonResponse, Service } from "./recommended_services"
import { RecentSearches } from "./recent_searches"
import { recommendationTemplate } from "./templates"
import { saveUserSearch, saveSearchTermBypassLength, createSearchItemFromElement, SearchEntryInterface } from "./search_history"
import { SearchHelperNavigation } from "./search_helper_navigation"

// Typing runs ~150-250ms per key, so 100ms sent a request for nearly every
// keystroke; this waits for a pause instead.
const SEARCH_INPUT_DEBOUNCE_MS = 250
// Past this, Enter gives up on the exact-name check and opens the results page,
// which lists an exact match first anyway.
const ENTER_SERVICES_WAIT_MS = 2000

export const UniversalSearchHeader = () => {
  const universalSearch = document.querySelector("#universal-search")
  if (!universalSearch) return

  // unhide the dropdown, hidden by default to avoid FOUC
  // unhide the search input, hidden by default on mobile
  universalSearch.parentElement?.removeAttribute("style")
  universalSearch.querySelector(".universal-search-helper")?.removeAttribute("style")

  const inputSearch = universalSearch.querySelector("input")
  const searchResultsContainer = universalSearch.querySelector(".search-preview > ul")

  RecentSearches()

  // Fetched on first interaction, not on page load: this feeds the Enter-key
  // exact-name match alone now that the tiles are server-rendered and the
  // dropdown is ranked by IFE. runWhenIdle was not enough -- its 2s timeout still
  // fired for crawlers, which then abandoned the request on navigation. Bots do
  // not interact with inputs.
  const MAX_SERVICE_FETCH_ATTEMPTS = 3

  let allServices: Service[] = []
  let servicesPromise: Promise<void> | null = null
  let serviceFetchAttempts = 0
  let searchGeneration = 0
  // The memo is cleared so the next interaction retries; the cap stops a hard-down
  // endpoint being hit once per focus for as long as the page is open.
  const resetServices = () => {
    if (serviceFetchAttempts >= MAX_SERVICE_FETCH_ATTEMPTS) return
    servicesPromise = null
  }
  const ensureServices = () => {
    if (!servicesPromise) {
      serviceFetchAttempts += 1
      // Settle on both outcomes: if this stayed pending, every Enter would hang
      // forever.
      servicesPromise = fetchAllServices().then(
        services => {
          allServices = services
          // fetchAllServices swallows its errors into [], so empty is
          // indistinguishable from failed -- and neither is a legitimate result.
          if (services.length === 0) resetServices()
        },
        () => resetServices(),
      )
    }
    return servicesPromise
  }

  SearchHelperNavigation(universalSearch)

  const fetchRankedServices = async (term: string): Promise<Service[]> => {
    const response = await fetch(`/explore/services/search/${encodeURIComponent(term.toLowerCase())}`)
    return isJsonResponse(response) ? response.json() : []
  }

  const triggerSearch = async (event: Event) => {
    const keyboardEvent = event as KeyboardEvent
    const searchTerm = (keyboardEvent.target as HTMLInputElement).value.trim()

    // SearchHelperNavigation already opened the highlighted result.
    if (!searchTerm || keyboardEvent.defaultPrevented) return

    if (keyboardEvent.key === "Enter") {
      window.App.Utils.logCustomDatadogAction("universal_search_trigger_via_enter", { term: searchTerm })

      // Reading allServices mid-flight would downgrade an exact match to a
      // results-page search, and names can be multi-word ("AI Agent").
      const generation = searchGeneration
      await Promise.race([ensureServices(), new Promise(resolve => setTimeout(resolve, ENTER_SERVICES_WAIT_MS))])
      // Edited while waiting: this Enter's term is no longer what's in the box.
      if (generation !== searchGeneration) return

      const serviceMatch = allServices.find(
        service =>
          service.name.toLowerCase() === searchTerm.toLowerCase() ||
          service.module_name.toLowerCase() === searchTerm.toLowerCase()
      )

      if (serviceMatch) {
        // Exact match with a service name, navigate to the service page
        saveUserSearch({
          type: "service",
          term: searchTerm,
          details: {
            name: serviceMatch.name,
            brand_color: serviceMatch.brand_color,
            module_name: serviceMatch.module_name,
            image: serviceMatch.image,
          }
        })
        window.location.assign(`${linkOrigin()}/${serviceMatch.module_name}`)
      } else {
        saveUserSearch({ type: "search", term: searchTerm })
        window.location.assign(`${linkOrigin()}/search/query/${encodeURIComponent(searchTerm.toLowerCase())}`)
      }
    }
  }

  const handleSearchInput = (searchResultsContainer: Element) => async (event: Event) => {
    const searchTerm = (event.target as HTMLInputElement).value.trim()
    const generation = searchGeneration

    if (!searchTerm) return

    let results: Service[] = []
    try {
      results = await fetchRankedServices(searchTerm)
    } catch {
      // Commonly a fetch aborted by navigation. The "see all results" link still works.
    }

    if (generation !== searchGeneration || !searchResultsContainer) return

    searchResultsContainer.innerHTML = results.map(service => Mustache.render(recommendationTemplate, { ...service, link_origin: linkOrigin() })).join("")
  }

  const recommendedServiceClick = (event: Event) => {
    const serviceAnchor = (event.target as HTMLElement).closest("li > a") as HTMLAnchorElement | null

    const searchEntry = createSearchItemFromElement(serviceAnchor) as SearchEntryInterface | null
    if (!searchEntry) return
    // Bypasses MINIMUM_SEARCH_TERM_LENGTH: this is a click on a known service,
    // not a typed query, and short names (MyQ) would otherwise never be recorded.
    saveSearchTermBypassLength(searchEntry)
    if (inputSearch.value.trim()) {
      // Click on filtered recommended services
      window.App.Utils.logCustomDatadogAction(`universal_search_service_filter_clicked`, {
        service: serviceAnchor?.dataset?.term,
      })
    } else {
      // On explore, clicking on recent search that is a service, the result is a service search (same from recommended service).
      // On header, even on recent search, the service click will navigate to the service page.
      if (searchEntry.type === "search") {
        window.App.Utils.logCustomDatadogAction(`universal_search_recent_searches_clicked`, {
          term: serviceAnchor?.dataset?.term,
        })
      } else {
        window.App.Utils.logCustomDatadogAction(`universal_search_recommended_service_clicked`, {
          service: serviceAnchor?.dataset?.term,
        })
      }
    }
  }

  const debouncedHandleInput = debounce(handleSearchInput(searchResultsContainer), SEARCH_INPUT_DEBOUNCE_MS)
  // Not debounced: any keystroke makes a response still in flight stale, and
  // an emptied box must not be refilled by one.
  addEventListenerWithKey(inputSearch, "input", "universal_search_header_input", (event: Event) => {
    searchGeneration += 1
    if (!(event.target as HTMLInputElement).value.trim() && searchResultsContainer) {
      searchResultsContainer.innerHTML = ""
    }
    debouncedHandleInput(event)
  })
  addEventListenerWithKey(inputSearch, "keydown", "universal_search_header_keydown", triggerSearch)

  const recentSearchesWrapper = universalSearch.querySelector(".recent-searches")
  if (recentSearchesWrapper) {
    addEventListenerWithKey(
      recentSearchesWrapper,
      "click",
      "universal_search_header_recent_click",
      recommendedServiceClick
    )
  }

  const recommendedServicesWrapper = universalSearch.querySelector(".recommended-services")
  if (recommendedServicesWrapper) {
    addEventListenerWithKey(
      recommendedServicesWrapper,
      "click",
      "universal_search_header_recommended_click",
      recommendedServiceClick
    )
  }

  const filteredServicesWrapper = universalSearch.querySelector(".search-preview")
  if (filteredServicesWrapper) {
    addEventListenerWithKey(
      filteredServicesWrapper,
      "click",
      "universal_search_header_filtered_click",
      recommendedServiceClick
    )
  }

  const searchNavigation = universalSearch.querySelector("a.search-navigation")
  if (searchNavigation) {
    addEventListenerWithKey(searchNavigation, "click", "universal_search_header_navigation_click", () => {
      window.App.Utils.logCustomDatadogAction("universal_search_trigger_via_navigation_link", {
        term: inputSearch?.value.trim(),
      })
    })
  }

  // Fires before focus for mouse users, and never for crawlers.
  addEventListenerWithKey(universalSearch, "pointerenter", "universal_search_header_pointerenter", () => {
    void ensureServices()
  })

  addEventListenerWithKey(inputSearch, "focus", "universal_search_header_focus", () => {
    // Warms the list so Enter on an exact name rarely waits for it.
    void ensureServices()
    window.App.Utils.logCustomDatadogAction("universal_search_click_search_bar")
  })

  const mobileIconSearch = document.querySelector("button.mobile-icon-search")
  if (mobileIconSearch) {
    addEventListenerWithKey(mobileIconSearch, "click", "universal_search_header_mobile_icon_click", () => {
      document.body.classList.add("mobile-universal-search-enabled")
      inputSearch?.focus()
      window.App.Utils.logCustomDatadogAction("mobile_universal_search_view")
    })
  }

  const closeMobileSearch = () => {
    document.body.classList.remove("mobile-universal-search-enabled")
    inputSearch?.blur()
    if (mobileIconSearch instanceof HTMLElement) mobileIconSearch.focus()
  }

  const mobileIconClose = document.querySelector(".mobile-close-search-icon")
  if (mobileIconClose) {
    addEventListenerWithKey(mobileIconClose, "click", "universal_search_header_mobile_close_click", closeMobileSearch)
  }

  // Capture runs before search_helper_navigation, so an Escape that only clears a highlighted
  // result still sees the highlight and keeps the overlay open.
  addEventListenerWithKey(
    document,
    "keydown",
    "universal_search_header_mobile_escape",
    (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      if (!document.body.classList.contains("mobile-universal-search-enabled")) return
      if (universalSearch.querySelector(".highlighted")) return
      closeMobileSearch()
    },
    { capture: true },
  )

  addEventListenerWithKey(window, "scroll", "universal_search_header_scroll", () => {
    if (!document.body.classList.contains("mobile-universal-search-enabled")) return
    inputSearch?.blur()
  })

  onBFCacheLoad("universal_search_header", () => {
    if (inputSearch) {
      inputSearch.value = ""
      inputSearch.dispatchEvent(new Event("input", { bubbles: true }))
    }
    if (searchResultsContainer) searchResultsContainer.innerHTML = ""

    UniversalSearchHeader()
  })
}
